import {
  EPaymentRequestFeature,
  EPaymentRequestStatus,
  EPayoutType,
} from "@rentiohq/shared/dist/types/payment.types";
import {
  endOfDay,
  startOfDay,
  subDays,
} from "@rentiohq/shared/dist/utils/date-fns.utils";
import { END_RENTIO_FOLLOW_UP_DSO } from "utils/payment";

const NOW = new Date();
const START_OF_DAY = startOfDay(NOW);

export enum EPaymentRequestFilterPhase {
  Incoming = "INCOMING",
  Rentio = "RENTIO",
  Action = "ACTION",
}

export enum EPaymentRequestFilterStatus {
  New = "new",
  Approved = "approved",
  Pending = "pending",
  Partial = "partial",
  Failed = "failed",
}

export enum EPaymentRequestFilterFrom {
  PayerDirect = "PAYER_DIRECT",
  BrokerThirdParty = "BROKER_THIRD_PARTY",
}

export const getPaymentRequestsFilter = (params: {
  propertyId?: number;
  search?: string;
  status?: EPaymentRequestFilterStatus;
  phase?: EPaymentRequestFilterPhase;
  from?: EPaymentRequestFilterFrom;
}) => {
  const { propertyId, search, status, phase, from } = params;

  let filters: {}[] = [];

  if (propertyId) {
    filters.push({ requestPropertyId: propertyId });
  }

  if (status) {
    let statuses: EPaymentRequestStatus[] = [];

    switch (status) {
      case EPaymentRequestFilterStatus.New:
        statuses = [EPaymentRequestStatus.New];

        break;

      case EPaymentRequestFilterStatus.Approved:
        statuses = [EPaymentRequestStatus.Approved];

        break;

      case EPaymentRequestFilterStatus.Pending:
        statuses = [
          EPaymentRequestStatus.Pending,
          EPaymentRequestStatus.PendingThirdParty,
          EPaymentRequestStatus.PayInPlanned,
          EPaymentRequestStatus.PaidIn,
          EPaymentRequestStatus.Transferring,
          EPaymentRequestStatus.Transferred,
          EPaymentRequestStatus.PayingOut,
        ];

        break;

      case EPaymentRequestFilterStatus.Partial:
        statuses = [EPaymentRequestStatus.Partial];

        break;

      case EPaymentRequestFilterStatus.Failed:
        statuses = [
          EPaymentRequestStatus.FailedPaidIn,
          EPaymentRequestStatus.FailedTransfer,
          EPaymentRequestStatus.FailedPayout,
          EPaymentRequestStatus.FailedKyc,
          EPaymentRequestStatus.Failed,
        ];

        break;

      default:
        break;
    }

    filters.push({
      status: { inq: statuses },
    });
  }

  if (phase) {
    switch (phase) {
      case EPaymentRequestFilterPhase.Incoming:
        filters.push({ dueDateAt: { gte: startOfDay(NOW.getTime()) } });

        break;

      case EPaymentRequestFilterPhase.Rentio:
        filters.push(
          ...[
            {
              dueDateAt: {
                gt: endOfDay(subDays(NOW, END_RENTIO_FOLLOW_UP_DSO)).getTime(),
              },
            },
            {
              dueDateAt: {
                lt: startOfDay(NOW).getTime(),
              },
            },
          ],
        );

        break;

      case EPaymentRequestFilterPhase.Action:
        filters.push({
          dueDateAt: {
            lte: endOfDay(subDays(NOW, END_RENTIO_FOLLOW_UP_DSO)).getTime(),
          },
        });

        break;
    }
  }

  if (from) {
    switch (from) {
      case EPaymentRequestFilterFrom.PayerDirect:
        filters.push({
          features: {
            nlike: `%${EPaymentRequestFeature.ThirdPartyPayInBrokerDirect}%`,
          },
        });

        break;

      case EPaymentRequestFilterFrom.BrokerThirdParty:
        filters.push({
          features: {
            like: `%${EPaymentRequestFeature.ThirdPartyPayInBrokerDirect}%`,
          },
        });

        break;

      default:
        break;
    }
  }

  return {
    order: "dueDateAt ASC",
    search,
    where: {
      or: [
        {
          and: [...filters, { status: { neq: EPaymentRequestStatus.Paid } }],
        },
        {
          and: [
            ...filters,

            { status: { eq: EPaymentRequestStatus.Paid } },

            { plannedExecutionAt: { gte: START_OF_DAY } },

            { payoutType: { eq: EPayoutType.Charge } },

            {
              features: {
                like: `%${EPaymentRequestFeature.PlannedPayOut}%`,
              },
            },
          ],
        },
      ],
    },
  };
};
